import { Box, Button, ButtonGroup } from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { Field, Form } from "react-final-form";
import { AdaptedSelect, InputControl, Optional } from "../components/Form";
import {
    Gender,
    LanguageCode,
    useCitiesLazyQuery,
    useCountriesQuery,
    useHealthCentersLazyQuery,
    useLanguagesQuery,
    useSpecialitiesQuery
} from "../graphql";

export interface ISpecialistSignupFormValues {
  email: string;
  phoneNumber: string;
  name: string;
  lastname: string;
  birthday: Date;
  sex: Gender;
  licenceNumber: string;
  bio: string;
  cityId: string;
  countryId: string;
  healthCenterId: string;
  languageCode: LanguageCode;
  specialityId: string;
}

interface ISpecialistSignupForm {
  onSubmit: (values: ISpecialistSignupFormValues) => void;
  initialValues?: Optional<ISpecialistSignupFormValues>;
}

export const SpecialistSignupForm: React.FC<ISpecialistSignupForm> = ({
    onSubmit,
    initialValues
}) => {
    const { data } = useLanguagesQuery();

    const [countryId, setCountryId] = useState();
    const [cityId, setCityId] = useState();

    const {
        loading: loadingCountries,
        data: { countries = [] } = {}
    } = useCountriesQuery();
    const [
        loadCities,
        { data: { cities = [] } = {}, loading: loadingCities }
    ] = useCitiesLazyQuery();
    const [
        loadHealthCenters,
        { data: { healthCenters = [] } = {}, loading: loadingHealthCenters }
    ] = useHealthCentersLazyQuery();

    const {
        data: specialitiesData,
        loading: loadingSpecialities
    } = useSpecialitiesQuery();

    const loading = loadingCountries || loadingCities || loadingHealthCenters;

    useEffect(() => {
        if (countryId) {
            loadCities({
                variables: {
                    countryId: parseInt(countryId)
                }
            });
        }
    }, [loadCities, countryId]);

    useEffect(() => {
        if (cityId) {
            loadHealthCenters({
                variables: {
                    cityId: parseInt(cityId)
                }
            });
        }
    }, [cityId, loadHealthCenters]);

    const validate = (values) => {
        const errors: { [key: string]: string } = {};

        if (!values.email) {
            errors.email = "Fushë e detyruar";
        }
        if (!values.phoneNumber) {
            errors.phoneNumber = "Fushë e detyruar";
        }
        if (!values.name) {
            errors.name = "Fushë e detyruar";
        }
        if (!values.lastname) {
            errors.lastname = "Fushë e detyruar";
        }
        if (!values.birthday) {
            errors.birthday = "Fushë e detyruar";
        }
        if (!values.sex) {
            errors.sex = "Fushë e detyruar";
        }
        if (!values.licenceNumber) {
            errors.licenceNumber = "Fushë e detyruar";
        }

        if (!values.cityId) {
            errors.cityId = "Fushë e detyruar";
        }
        if (!values.countryId) {
            errors.countryId = "Fushë e detyruar";
        }
        if (!values.healthCenterId) {
            errors.healthCenterId = "Fushë e detyruar";
        }
        if (!values.languageCode) {
            errors.languageCode = "Fushë e detyruar";
        }

        return errors;
    };

    return (
        <Form
            onSubmit={ onSubmit }
            validate={ validate }
            initialValues={ initialValues }
            render={ ({
                handleSubmit,
                form,
                errors,
                submitting,
                pristine,
                values
            }) => (
                <Box as="form" onSubmit={ handleSubmit }>
                    <InputControl type="text" name="name" label="Emri" />

                    <InputControl type="text" name="lastname" label="Mbiemri" />

                    <Field name="sex" component={ AdaptedSelect } label="Gjinia">
                        <option value="MALE">Mashkull</option>
                        <option value="FEMALE">Femër</option>
                    </Field>
                    <InputControl type="date" name="birthday" label="Datëlindja" />

                    <InputControl
                        type="text"
                        name="phoneNumber"
                        label="Numri i celularit"
                    />

                    <InputControl type="email" name="email" label="Email" />

                    <Field
                        name="countryId"
                        component={ AdaptedSelect }
                        label="Shteti"
                        disabled={ loading }
                        onChange={ (e) => setCountryId(e.target.value) }
                        defaultValue={ countryId }
                    >
                        { countries.map((country) => (
                            <option value={ country.id }>{ country.name }</option>
                        )) }
                    </Field>

                    <Field
                        name="cityId"
                        component={ AdaptedSelect }
                        label="Qyteti"
                        disabled={ loading }
                        onChange={ (e) => setCityId(e.target.value) }
                        defaultValue={ cityId }
                    >
                        { cities.map((country) => (
                            <option value={ country.id }>{ country.name }</option>
                        )) }
                    </Field>

                    <Field
                        name="healthCenterId"
                        component={ AdaptedSelect }
                        label="Qëndra shëndetsore"
                        disabled={ loading }
                    >
                        { healthCenters.map((country) => (
                            <option value={ country.id }>{ country.name }</option>
                        )) }
                    </Field>

                    <InputControl
                        type="text"
                        name="licenceNumber"
                        label="Numri i licensës"
                    />

                    <Field
                        name="specialityId"
                        component={ AdaptedSelect }
                        label="Specialiteti"
                        isDisabled={ loadingSpecialities }
                    >
                        { specialitiesData?.specialities.map((speciality) => (
                            <option value={ speciality.id }>{ speciality.name }</option>
                        )) }
                    </Field>

                    <Field
                        name="languageCode"
                        component={ AdaptedSelect }
                        label="Gjuha"
                        disabled={ loading }
                    >
                        { data?.languages?.map((language) => (
                            <option value={ language.code }>{ language.name }</option>
                        )) }
                    </Field>

                    <ButtonGroup spacing={ 4 }>
                        <Button
                            isLoading={ submitting }
                            loadingText="Duke dërguar"
                            variantColor="teal"
                            type="submit"
                        >
                            Regjistro
                        </Button>
                        <Button
                            variantColor="teal"
                            variant="outline"
                            onClick={ form.reset }
                            isDisabled={ submitting || pristine }
                        >
                            Pastro
                        </Button>
                    </ButtonGroup>
                </Box>
            ) }
        />
    );
};
